import { getChoiceQuantityIncrement, getEffectiveModSelectedByDefaultQuantity, getMaxAggregateQuantity, getMaxSelectable, getMinAggregateQuantity, getMinChoiceQuantity, getMinSelectable, getModMaxChoiceQuantity, modIsSelectedByDefault } from "../utils/mod_group_utils.js";
import "../utils/index.js";
//#region src/services/required_mods_price_calculator.ts
let RequiredModsPriceCalculator;
(function(_RequiredModsPriceCalculator) {
	function getModPriceMinimum(modGroup, i, modGroupById, modById, sortOptions, modGroupPriceById) {
		const modRef = modGroup.items[i];
		const i9nModRef = modGroup.i9nItems?.[i];
		const mod = modById[modRef._id.toString()];
		const modPriceOverride = modRef.price ?? i9nModRef?.price;
		const minPricesDeep = mod?.priceOptions.map((po) => {
			const deeperModsPrice = (po.addonSetIds ?? po.modGroupIds)?.reduce((acc, modGroupId) => {
				const key = modGroupId.toString();
				const deeperModGroup = modGroupById[key];
				if (deeperModGroup) {
					let total = modGroupPriceById.get(key);
					if (total === void 0) {
						modGroupPriceById.set(key, 0);
						total = findMinimumDeep(deeperModGroup, modGroupById, modById, sortOptions, modGroupPriceById).reduce((sum, price) => sum + price, 0);
						modGroupPriceById.set(key, total);
					}
					acc += total;
				}
				return acc;
			}, 0) ?? 0;
			return (modPriceOverride ?? po.price) + deeperModsPrice;
		});
		const minPrice = minPricesDeep?.length ? Math.min(...minPricesDeep) : 0;
		return sortOptions.treatNegativePricesAsFree ? Math.max(0, minPrice) : minPrice;
	}
	function sortModPrices(modGroup, modGroupById, modById, sortOptions, modGroupPriceById) {
		const modIndexes = [...Array(modGroup.items.length).keys()];
		const choiceQuantityIncrement = getChoiceQuantityIncrement(modGroup);
		const maxAggregateQuantity = getMaxAggregateQuantity(modGroup);
		const modPriceByIndex = /* @__PURE__ */ new Map();
		modGroup.items.forEach((modRef, index) => {
			const maxChoiceQuantity = Math.min(getModMaxChoiceQuantity(modGroup, modRef._id), maxAggregateQuantity);
			const maxChoiceQuantityAdjusted = Math.floor(maxChoiceQuantity / choiceQuantityIncrement) * choiceQuantityIncrement;
			modPriceByIndex.set(index, {
				price: getModPriceMinimum(modGroup, index, modGroupById, modById, sortOptions, modGroupPriceById),
				maxChoiceQuantity: maxChoiceQuantityAdjusted,
				selectedByDefaultQuantity: getEffectiveModSelectedByDefaultQuantity(modGroup, modRef._id)
			});
		});
		return modIndexes.sort((modAIndex, modBIndex) => {
			const { price: modAPrice } = modPriceByIndex.get(modAIndex);
			const { price: modBPrice } = modPriceByIndex.get(modBIndex);
			if (!sortOptions.sortDefaultBeforePriced) return modAPrice - modBPrice;
			if (modAPrice <= 0 && modBPrice > 0) return -1;
			if (modBPrice <= 0 && modAPrice > 0) return 1;
			const modARef = modGroup.items[modAIndex];
			const modBRef = modGroup.items[modBIndex];
			const modAIsSelectedByDefault = modIsSelectedByDefault(modGroup, modARef._id);
			const modBIsSelectedByDefault = modIsSelectedByDefault(modGroup, modBRef._id);
			if (modAIsSelectedByDefault && !modBIsSelectedByDefault) return -1;
			if (modBIsSelectedByDefault && !modAIsSelectedByDefault) return 1;
			return modAPrice - modBPrice;
		}).map((index) => modPriceByIndex.get(index));
	}
	function findMinimumShallow(modGroup, modById, sortOptions) {
		return findMinimumDeep(modGroup, {}, modById, sortOptions);
	}
	_RequiredModsPriceCalculator.findMinimumShallow = findMinimumShallow;
	function findMinimumDeep(modGroup, modGroupById, modById, sortOptions, modGroupPriceById = /* @__PURE__ */ new Map()) {
		const minSelectable = getMinSelectable(modGroup);
		const minAggregateQuantity = getMinAggregateQuantity(modGroup);
		if (minSelectable === 0 && minAggregateQuantity === 0) return [];
		const sortedPrices = sortModPrices(modGroup, modGroupById, modById, sortOptions, modGroupPriceById);
		if (sortedPrices.length === 0) return [];
		if (canUseGreedy(modGroup, sortedPrices)) return findMinimumGreedy(modGroup, sortOptions, sortedPrices);
		return findMinimumDynamic(modGroup, sortOptions, sortedPrices);
	}
	_RequiredModsPriceCalculator.findMinimumDeep = findMinimumDeep;
	function canUseGreedy(modGroup, sortedPrices) {
		if (sortedPrices.length === 0) return true;
		if (getMinChoiceQuantity(modGroup) > getChoiceQuantityIncrement(modGroup)) return false;
		if (getMaxSelectable(modGroup) < sortedPrices.length) {
			const firstMax = sortedPrices[0].maxChoiceQuantity;
			if (sortedPrices.some(({ maxChoiceQuantity }) => maxChoiceQuantity !== firstMax)) return false;
		}
		return true;
	}
	function findMinimumDynamic(modGroup, sortOptions, sortedPrices) {
		const minSelectable = getMinSelectable(modGroup);
		const minAggregateQuantity = getMinAggregateQuantity(modGroup);
		const maxSelectable = getMaxSelectable(modGroup);
		const maxAggregateQuantity = getMaxAggregateQuantity(modGroup);
		const choiceQuantityIncrement = getChoiceQuantityIncrement(modGroup);
		const minChoiceQuantity = Math.ceil(getMinChoiceQuantity(modGroup) / choiceQuantityIncrement) * choiceQuantityIncrement;
		const realMinAggregateQuantity = Math.max(minAggregateQuantity, minChoiceQuantity * minSelectable);
		let pricesForSelection = sortedPrices;
		const validChoicePrices = sortedPrices.filter(({ maxChoiceQuantity }) => maxChoiceQuantity >= minChoiceQuantity);
		const validChoiceCapacity = [...validChoicePrices].sort((modA, modB) => modB.maxChoiceQuantity - modA.maxChoiceQuantity).slice(0, maxSelectable).reduce((total, { maxChoiceQuantity }) => total + maxChoiceQuantity, 0);
		if (validChoicePrices.length >= minSelectable && validChoiceCapacity >= minAggregateQuantity) pricesForSelection = validChoicePrices;
		/**
		* The smallest feasible aggregate quantity that satisfies the minimum aggregate and
		* selectable constraints. The total must be rounded up to a complete minimum-choice block.
		*
		* This prevents the candidate search from exploring quantities beyond the amount required by
		* the group's minimum-selection constraints.
		*
		* This will prevent mods with a negative price from being considered beyond what is necessary
		* to satisfy the minimum aggregate quantity.
		*/
		const minimumFeasibleAggregateQuantity = minChoiceQuantity > 0 ? Math.ceil(realMinAggregateQuantity / minChoiceQuantity) * minChoiceQuantity : realMinAggregateQuantity;
		const aggregateQuantitySearchLimit = Math.ceil(minimumFeasibleAggregateQuantity / choiceQuantityIncrement) * choiceQuantityIncrement;
		/**
		* Upper bound for aggregate quantities considered by the candidate search.
		*
		* This is limited by both the configured/search-derived aggregate maximum and the greatest
		* quantity that can be produced by at most `maxSelectable` modifiers. It prevents generating
		* impossible candidates and avoids exploring unnecessary additional quantities, especially for
		* negative-priced modifiers.
		*
		* This bound may be below `minAggregateQuantity` when the group's constraints are impossible.
		* The calculator then retains its existing partial-candidate fallback behavior.
		*/
		const maxAggregateQuantitySearchBound = Math.min(Math.min(maxAggregateQuantity, aggregateQuantitySearchLimit), [...pricesForSelection].sort((modA, modB) => modB.maxChoiceQuantity - modA.maxChoiceQuantity).slice(0, maxSelectable).reduce((total, { maxChoiceQuantity }) => total + maxChoiceQuantity, 0));
		const candidatesMap = /* @__PURE__ */ new Map([["0:0", {
			prices: [],
			selected: 0,
			aggregate: 0,
			totalPrice: 0,
			defaultAggregate: 0,
			defaultOrNonPositiveAggregate: 0,
			nonPositiveAggregate: 0,
			positiveAggregate: 0
		}]]);
		for (const { price, maxChoiceQuantity, selectedByDefaultQuantity } of pricesForSelection) {
			const nextCandidatesMap = new Map(candidatesMap);
			for (const candidate of candidatesMap.values()) {
				if (candidate.selected >= maxSelectable) continue;
				/**
				* The minimum quantity of the current mod to consider for selection.
				* If the maximum exceeds the minimum, use the max as the min as a best-effort lower bound.
				*/
				const realMinChoiceQuantity = Math.min(minChoiceQuantity, maxChoiceQuantity);
				/**
				* Maximum quantity of the current modifier that can be added to this candidate while
				* respecting the modifier and aggregate quantity limits.
				*/
				const maxQuantityToAdd = Math.min(maxChoiceQuantity, maxAggregateQuantitySearchBound - candidate.aggregate);
				/**
				* The max quantity after taking the choice increment into account.
				* If the max quantity was 7, but the choice quantity increment is 3, then the guest is
				* effectively limited to 6.
				*/
				const maxQuantityToAddRounded = Math.floor(maxQuantityToAdd / choiceQuantityIncrement) * choiceQuantityIncrement;
				for (let quantity = maxQuantityToAddRounded; quantity >= realMinChoiceQuantity; quantity -= choiceQuantityIncrement) {
					const nextPrices = [...candidate.prices];
					for (let i = 0; i < quantity; ++i) {
						/**
						* True if this modifier is selected by default.
						* If the modifier has a minimum choice quantity, then the first few selections up to
						* that minimum are considered default.
						*/
						const isDefault = Boolean(selectedByDefaultQuantity) && i < Math.max(selectedByDefaultQuantity, realMinChoiceQuantity);
						/**
						* True if this modifier is selected to meet the minimum selectable requirement.
						* Since the guest must select at least the minimum choice quantity, the first few
						* selections up to that minimum are considered to meet the minimum requirement.
						*/
						const isMinSelected = Boolean(minSelectable) && candidate.selected < minSelectable && i < realMinChoiceQuantity;
						nextPrices.push({
							value: price,
							isDefault,
							isMinSelected
						});
					}
					const nextDefaultAggregate = candidate.defaultAggregate + Math.min(quantity, selectedByDefaultQuantity);
					const nextNonPositiveAggregate = candidate.nonPositiveAggregate + (price <= 0 ? quantity : 0);
					const nextPositiveAggregate = candidate.positiveAggregate + (price > 0 ? quantity : 0);
					const nextCandidate = {
						prices: nextPrices,
						selected: candidate.selected + 1,
						aggregate: candidate.aggregate + quantity,
						totalPrice: candidate.totalPrice + quantity * price,
						defaultAggregate: Math.min(realMinAggregateQuantity, nextDefaultAggregate),
						defaultOrNonPositiveAggregate: Math.min(realMinAggregateQuantity, nextDefaultAggregate + nextNonPositiveAggregate),
						nonPositiveAggregate: nextNonPositiveAggregate,
						positiveAggregate: nextPositiveAggregate
					};
					const key = `${nextCandidate.selected}:${nextCandidate.aggregate}`;
					const existingCandidate = nextCandidatesMap.get(key);
					function isNextCandidateBetter() {
						if (!existingCandidate) return true;
						if (sortOptions.sortDefaultBeforePriced) {
							if (nextCandidate.defaultOrNonPositiveAggregate !== existingCandidate.defaultOrNonPositiveAggregate) return nextCandidate.defaultOrNonPositiveAggregate > existingCandidate.defaultOrNonPositiveAggregate;
							if (nextCandidate.nonPositiveAggregate && existingCandidate.nonPositiveAggregate && nextCandidate.positiveAggregate && existingCandidate.positiveAggregate && nextCandidate.defaultAggregate !== existingCandidate.defaultAggregate) return nextCandidate.defaultAggregate > existingCandidate.defaultAggregate;
						}
						return nextCandidate.totalPrice < existingCandidate.totalPrice;
					}
					if (isNextCandidateBetter()) nextCandidatesMap.set(key, nextCandidate);
				}
			}
			candidatesMap.clear();
			for (const [key, candidate] of nextCandidatesMap) candidatesMap.set(key, candidate);
		}
		const candidatesArray = Array.from(candidatesMap.values());
		let bestCandidate = candidatesArray.shift();
		function isCandidateValid(candidate) {
			return candidate.selected >= minSelectable && candidate.selected <= maxSelectable && candidate.aggregate >= minAggregateQuantity && candidate.aggregate <= maxAggregateQuantity;
		}
		function isBetterCandidate(candidate) {
			const currentIsValid = isCandidateValid(candidate);
			if (currentIsValid !== isCandidateValid(bestCandidate)) return currentIsValid;
			if (!currentIsValid) {
				if (candidate.selected !== bestCandidate.selected) return candidate.selected > bestCandidate.selected;
				return candidate.aggregate > bestCandidate.aggregate;
			}
			if (sortOptions.sortDefaultBeforePriced) {
				if (candidate.nonPositiveAggregate && bestCandidate.nonPositiveAggregate && candidate.positiveAggregate && bestCandidate.positiveAggregate) {
					if (candidate.defaultAggregate !== bestCandidate.defaultAggregate) return candidate.defaultAggregate > bestCandidate.defaultAggregate;
					if (candidate.totalPrice !== bestCandidate.totalPrice) return candidate.totalPrice < bestCandidate.totalPrice;
					if (candidate.aggregate !== bestCandidate.aggregate) return candidate.aggregate < bestCandidate.aggregate;
					return false;
				}
				if (candidate.defaultOrNonPositiveAggregate !== bestCandidate.defaultOrNonPositiveAggregate) return candidate.defaultOrNonPositiveAggregate > bestCandidate.defaultOrNonPositiveAggregate;
				if (candidate.nonPositiveAggregate && candidate.aggregate !== bestCandidate.aggregate) return candidate.aggregate < bestCandidate.aggregate;
			}
			if (candidate.totalPrice !== bestCandidate.totalPrice) return candidate.totalPrice < bestCandidate.totalPrice;
			return candidate.aggregate < bestCandidate.aggregate;
		}
		for (const candidate of candidatesArray) if (isBetterCandidate(candidate)) bestCandidate = candidate;
		return bestCandidate.prices.sort((a, b) => {
			if (!sortOptions.sortDefaultBeforePriced) {
				if (a.isMinSelected && !b.isMinSelected) return -1;
				if (!a.isMinSelected && b.isMinSelected) return 1;
				return a.value - b.value;
			}
			if (a.value <= 0 || b.value <= 0) {
				if (a.isMinSelected && !b.isMinSelected) return -1;
				if (!a.isMinSelected && b.isMinSelected) return 1;
				return a.value - b.value;
			}
			if (a.isDefault && !b.isDefault) return -1;
			if (!a.isDefault && b.isDefault) return 1;
			if (a.isMinSelected && !b.isMinSelected) return -1;
			if (!a.isMinSelected && b.isMinSelected) return 1;
			return a.value - b.value;
		}).map((priceObj) => priceObj.value);
	}
	function findMinimumGreedy(modGroup, sortOptions, sortedPrices) {
		if (sortedPrices.length === 0) return [];
		const choiceQuantityIncrement = getChoiceQuantityIncrement(modGroup);
		const maxSelectable = Math.min(getMaxSelectable(modGroup), sortedPrices.length);
		const minSelectable = Math.min(getMinSelectable(modGroup), sortedPrices.length, maxSelectable);
		const minAggregateQuantity = getMinAggregateQuantity(modGroup);
		const maxAggregateQuantity = getMaxAggregateQuantity(modGroup);
		const requiredModPrices = [];
		const modInfos = sortedPrices.map((modInfo) => {
			const maxChoiceQuantity = Math.floor(modInfo.maxChoiceQuantity / choiceQuantityIncrement) * choiceQuantityIncrement;
			const minChoiceQuantity = Math.ceil(Math.min(getMinChoiceQuantity(modGroup), maxChoiceQuantity) / choiceQuantityIncrement) * choiceQuantityIncrement;
			return {
				...modInfo,
				count: 0,
				minChoiceQuantity,
				maxChoiceQuantity
			};
		});
		let totalSelected = 0;
		function pushModInfo(modInfo) {
			if (totalSelected === maxSelectable && modInfo.count === 0) return false;
			if (requiredModPrices.length + choiceQuantityIncrement > maxAggregateQuantity) return false;
			if (modInfo.count + choiceQuantityIncrement > modInfo.maxChoiceQuantity) return false;
			for (let k = 0; k < choiceQuantityIncrement; ++k) {
				requiredModPrices.push(modInfo.price);
				if (modInfo.count === 0) ++totalSelected;
				++modInfo.count;
			}
			return true;
		}
		for (const modInfo of modInfos) {
			if (totalSelected >= minSelectable) break;
			for (let j = 0; j < modInfo.minChoiceQuantity; j += choiceQuantityIncrement) if (!pushModInfo(modInfo)) break;
		}
		if (requiredModPrices.length >= minAggregateQuantity) return requiredModPrices;
		const indexOfLastNonPositivePrice = modInfos.findLastIndex((modInfo) => modInfo.price <= 0);
		const nonPositivePrices = modInfos.slice(0, indexOfLastNonPositivePrice + 1);
		function addMinimumModsToRequired(mods) {
			for (let i = 0; i < mods.length; ++i) {
				const mod = mods[i];
				if (mod.count > 0) for (let j = mod.count; j < mod.maxChoiceQuantity; j += choiceQuantityIncrement) {
					if (!pushModInfo(mod)) break;
					if (requiredModPrices.length >= minAggregateQuantity) return true;
				}
				else if (totalSelected < maxSelectable) {
					for (let j = 0; j < mod.minChoiceQuantity; j += choiceQuantityIncrement) if (!pushModInfo(mod)) break;
					if (requiredModPrices.length >= minAggregateQuantity) return true;
					for (let j = mod.count; j < mod.maxChoiceQuantity; j += choiceQuantityIncrement) {
						if (!pushModInfo(mod)) break;
						if (requiredModPrices.length >= minAggregateQuantity) return true;
					}
				}
			}
			return false;
		}
		if (addMinimumModsToRequired(nonPositivePrices)) return requiredModPrices;
		if (sortOptions.sortDefaultBeforePriced) for (let i = 0; i < modInfos.length; ++i) {
			const modInfo = modInfos[i];
			if (!modInfo.selectedByDefaultQuantity) continue;
			const minimumDefaultQuantity = Math.max(modInfo.minChoiceQuantity, modInfo.selectedByDefaultQuantity);
			const targetQuantity = Math.min(modInfo.maxChoiceQuantity, minimumDefaultQuantity);
			while (modInfo.count < targetQuantity) {
				if (!pushModInfo(modInfo)) break;
				if (requiredModPrices.length >= minAggregateQuantity && modInfo.count >= modInfo.minChoiceQuantity) return requiredModPrices;
			}
		}
		if (addMinimumModsToRequired(modInfos.slice(indexOfLastNonPositivePrice + 1).sort((a, b) => a.price - b.price))) return requiredModPrices;
		return requiredModPrices;
	}
})(RequiredModsPriceCalculator || (RequiredModsPriceCalculator = {}));
//#endregion
export { RequiredModsPriceCalculator };

//# sourceMappingURL=required_mods_price_calculator.js.map